import { useMemo } from 'react';
import { Box, SimpleGrid } from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { TFunction } from 'i18next';
import { IconTrash } from '@tabler/icons-react';

import { Button, InputLabel, Link, NumberInput, Select, Text, YesNoRadioButton } from 'components';
import { AppUtils } from 'utils';
import { CreditResponse } from 'types';

interface CreditRequestPersonalChildrenProps {
  disabled?: boolean;
  form: UseFormReturnType<CreditResponse>;
  setValues?: (val: any) => void;
  t: TFunction;
}

interface ChildObject {
  creditRequestId?: number;
  birthYear?: Date,
  craft?: boolean,
  craftAmount?: number;
}

const tempChildObject = {
  creditRequestId: undefined,
  birthYear: undefined,
  craft: undefined,
  craftAmount: undefined
} as ChildObject;

const CreditRequestPersonalChildren = ({
  disabled,
  form,
  setValues,
  t,
}: CreditRequestPersonalChildrenProps) => {
  const propertyName = 'children';

  const childrenForm = useForm({
    mode: 'uncontrolled',
    initialValues: form.values[propertyName] && form.values[propertyName].length > 0 ? form.values[propertyName].map(el => ({ ...el })) : [{ ...tempChildObject, creditRequestId: form.values.id }],
    onValuesChange(values, _previous) {
      if (!setValues) {
        return;
      }
      if (values[values.length - 1] !== tempChildObject) {
        let childrenValue = Object.values(values);
        if (childrenValue) {
          childrenValue = childrenValue.map((child: any) => {
            const canAttendCraft = child.birthYear ? new Date().getFullYear() - child.birthYear >= 16 : false;
            const updatedChild = { ...child };
    
            if (!canAttendCraft) {
              updatedChild.craft = undefined;
              updatedChild.craftAmount = undefined;
            } else if (updatedChild.craft === false) {
              updatedChild.craftAmount = undefined;
            }
            return updatedChild;
          });
        }
        setValues({ [propertyName]: childrenValue });
      }
    },
  });

  const canAttendCraft = (index: number) => {
    const year = childrenForm.getInputProps(`${index}.birthYear`).value ?? childrenForm.getInputProps(`${index}.birthYear`).defaultValue;
    return year ? new Date().getFullYear() - year >= 16 : false;
  }

  const getChildrenValidYears = () => {
    const birthYear = form.values.birthDate?.getFullYear() ?? (new Date().getFullYear() - 25);
    const currentYear = new Date().getFullYear();
    const minYear = Math.max(birthYear, currentYear - 25);
    return Array.from(
      { length: currentYear - minYear + 1 },
      (_, i) => minYear + i
    );
  }

  const childrenView = useMemo(() => {
    const transformed = childrenForm.getTransformedValues();
    const length = Object.keys(transformed).length;
    return Object.keys(transformed).map((el: any, index: number) => (
      <Box key={`child-${index}`}>
        <InputLabel label={t('input.children.label', { number: (index + 1) })} />
        <SimpleGrid cols={4} mt={'sm'}>
          <Select
            label={t('input.children.birthYear.label')}
            data={getChildrenValidYears().map(el => (
              { value: el.toString(), label: el.toString() }
            ))}
            key={childrenForm.key(`${index}.birthYear`)}
            {...childrenForm.getInputProps(`${index}.birthYear`)}
            value={
              childrenForm.getInputProps(`${index}.birthYear`).defaultValue?.toString()
            }
            clearable={true}
            disabled={disabled}
          />
          {canAttendCraft(index) ?
            <>
              <YesNoRadioButton
                label={t('input.children.craft.label')}
                name={`${index}.craft`}
                form={childrenForm}
                t={t}
                disabled={disabled}
              />
              {childrenForm.getInputProps(`${index}.craft`).value || childrenForm.getInputProps(`${index}.craft`).defaultValue ?
                <NumberInput
                  label={t('input.children.craftAmount.label')}
                  key={childrenForm.key(`${index}.craftAmount`)}
                  {...childrenForm.getInputProps(`${index}.craftAmount`)}
                  min={0}
                  rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
                  rightSectionWidth={40}
                  disabled={disabled}
                />
              : <Box/>}
              </>
            : <><Box/><Box/></>
          }
          {length > 1 && !disabled &&
            <Box style={{ alignSelf: 'flex-end' }}>
              <Button
                variant={'transparent'}
                size={'compact-sm'}
                color={'var(--mantine-color-error)'}
                mb={6}
                onClick={() => AppUtils.removeObject(childrenForm, index, { ...tempChildObject, creditRequestId: form.values.id })}
              >
                <IconTrash size={20} />
              </Button>
            </Box>
          }
        </SimpleGrid>
      </Box>
    ))
  }, [childrenForm, disabled]);

  const addNewButton = useMemo(() => {
    const transformedLength = Object.keys(childrenForm.getTransformedValues()).length;
    if (transformedLength >= 4 || disabled) {
      return <></>;
    }
    return (
      <Link
        onClick={() => AppUtils.addNewObject(childrenForm, { ...tempChildObject, creditRequestId: form.values.id })}
        mt={'md'}
        c={'#2F6F16'}
      >
        {t('input.childrenBirthYear.link')}
      </Link>
    )
  }, [form, disabled]);

  return (
    <>
      <YesNoRadioButton
        name={'supportsChildren'}
        label={t('input.supportsChildren.label')}
        t={t}
        form={form}
        disabled={disabled}
      />
      {form.values.supportsChildren ?
        <>
          {childrenView}
          {addNewButton}
        </>
      : undefined}
    </>
  )
}

export default CreditRequestPersonalChildren;